import React from "react"

import ReviewCard from "../components/ReviewCard"
import { VideoPlayerProvider } from "../components/VideoPlayerContext"

/*
Flexible reviews grid. Accepts heterogeneous items from:
- Internal reviews (Review model)
- Video testimonials (videoUrl) - automatically generates preview from first frame
- External platforms (Trustpilot, App Store, Google Play, etc.)

Input shape per item (duck-typed):
{
  source: "internal" | "video" | "trustpilot" | "app_store" | "google_play" | "facebook" | "instagram" | string,
  rating: number | null,
  title?: string,
  body?: string,
  authorName?: string,
  profession?: string,
  locationAndDate?: string,
  avatarUrl?: string,
  imageUrl?: string, // optional fallback for video card background
  videoUrl?: string
}
*/

const ReviewsSection = ({ reviews = [] }) => {
  const [revealHeight, setRevealHeight] = React.useState(0)
  const showMoreButtonRef = React.useRef(null)
  const gridRef = React.useRef(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [visibleHeight, setVisibleHeight] = React.useState(0)
  const [gridContentHeight, setGridContentHeight] = React.useState(0)

  // Static mixed array with videos every 4 text reviews
  const mixedReviewsArray = React.useMemo(() => {
    // Static array with mixed sources and videos placed every 4 text reviews
    const fallbackReviews = [
      {
        source: "video",
        rating: 5,
        title: null,
        body: null,
        authorName: "Candice Van Dertholen",
        profession: "Energy Medicine Practitioner",
        locationAndDate: "Charlotte, NC",
        avatarUrl: null,
        imageUrl:
          "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/candice-van-dertholen-preview.png",
        videoUrl:
          "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/candice-van-dertholen.mp4"
      },
      {
        source: "facebook",
        rating: null,
        authorName: "Anita Stechschulte",
        profession: "Functional Medicine Practitioner",
        locationAndDate: "Ottawa, OH",
        body: "I LOVE being a healer on Heal.me! Highly recommend.",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/anita-stechschulte.png"
      },
      {
        source: "video",
        rating: 5,
        title: null,
        body: null,
        authorName: "Phyllis Nunn",
        profession: "Massage & craniosacral therapist",
        locationAndDate: "Paducah, KY • Jun 28, 2025",
        avatarUrl: null,
        imageUrl:
          "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/phyllis-nunn-preview.png",
        videoUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/phyllis-nunn.mp4"
      },
      {
        source: "google_play",
        rating: 5,
        authorName: "Cynthia Menard",
        profession: "Reiki Master",
        locationAndDate: "Westford, MA",
        body: "As an energy healer I think this is great - bringing holistic healers together in one place for clients to find. I appreciate having client management software integrated into a platform that helps me market my practice. Thank you!",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/cynthia-menard.png"
      },
      {
        source: "video",
        rating: 5,
        title: null,
        body: null,
        authorName: "Cameron Cietek & Sam Glenn",
        profession: "Neuromuscular Laser Therapy",
        locationAndDate: "Savannah, Georgia",
        avatarUrl: null,
        imageUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/nlt-preview.png",
        videoUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/nlt.mp4"
      },
      {
        source: "facebook",
        rating: null,
        authorName: "Michelle Zamora",
        profession: "Acupuncture Physician",
        locationAndDate: "Melbourne, FL",
        body: "I am on here and love it!",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/michelle-zamora.png"
      },
      {
        source: "instagram",
        rating: null,
        authorName: "Cristen Gajeda",
        profession: "Energy Healing Practitioner",
        locationAndDate: "Dallas, TX",
        body: "Registered ❤️",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/cristen-gajeda.png"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Jill Jones",
        profession: "Yoga Therapist",
        locationAndDate: "Collinsville, IL",
        body: "Great company to work with – I would give them 10 stars if possible. They create a beautiful product, and it speaks to the heart! I love my new website, and could not be happier, as the people are so kind and cooperative and want to do the best for their clients. I highly recommend using this platform if you are in the alternative healing arts!",
        title: "Great company to work with / Great product",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/jill-jones.png"
      },
      {
        source: "google_play",
        rating: 5,
        authorName: "Tai Davis",
        profession: "Reiki Master",
        locationAndDate: "Tucker, GA",
        body: "It's been working pretty well since the app was updated. It's so much more convenient to use the app to manage appointments. Thumbs up! I hope to see new improvements in the future.",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/tai-davis.png"
      },
      {
        source: "facebook",
        rating: null,
        authorName: "Joseph Velilla",
        profession: "Massage Therapist",
        locationAndDate: "Sanford, FL",
        body: "Highly recommended. It was a great process to be listed as a Holistic Provider in Central Florida",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/joseph-velilla.png"
      },

      {
        source: "trustpilot",
        rating: 5,
        authorName: "Ann Flynn",
        profession: "Reiki Master/ Sound Healer",
        locationAndDate: "Malverne, NY",
        body: "Heal.me is an excellent platform for holistic practitioners. They offer personal assistance, support, and honest communication. They are willing to work with you in a friendly, yet professional manner. I have witnessed Heal.me continually improve making it easier to build your business and clientele. The personal website is awesome & professional. You really can't go wrong.",
        title: "You can't go wrong!",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/ann-flynn.png"
      },
      {
        source: "google_play",
        rating: 5,
        authorName: "Watson Will",
        profession: "Master Hypnotherapist",
        locationAndDate: "Los Angeles, CA",
        body: "I was using calendly, google calender, and my website, and now I am switching everything over to this. Much easier, thank you!",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/watson-will.png"
      },
      {
        source: "instagram",
        rating: null,
        authorName: "Jvon Johnson",
        profession: "Certified Reiki Master",
        locationAndDate: "Chicago, IL",
        body: "I am on there!",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/jvon-johnson.png"
      },
      {
        source: "facebook",
        rating: null,
        authorName: "Maddison Sutton",
        profession: "Integrative Nutrition Health Coach",
        locationAndDate: "Charlotte, NC",
        body: "Heal.me is a great resource for anyone looking to heal holistically ❤️ For any women that are looking to overcome stress and hormone related conditions, I would love to support you!",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/maddison-sutton.png"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Carolyn Ruutelmann",
        profession: "Sound Bath and Reiki Practitioner",
        locationAndDate: "Naperville, Illinois",
        body: "Heal.me has been a wonderful experience for me & my Energy Therapy business. Just like all of life there have been a couple of snags along the way, but nothing too complicated. What has made the experience so fabulous is the reps that I have worked with from the initial exploration call with Winn (no pressure just information & answering questions) to building my profile with Ange (thanks for being so patient & helpful) to having my website built by Chad (such an incredible job thank you so so much Chad!!!! 💖👏) to a review call with Billy to improve my profile (so grateful Billy! 🙌).\r\n\r\nAll have been so kind and helpful and know their sh!t really well and are great at what they do! This team of folks seem to just want their providers/practitioners on their site to be successful in their respective businesses.\r\n\r\nHeal.me focuses on the promoting so that you, the practitioner, can focus on your practice. It is a growing site so there are transitions. They are also working on upgrades to improve things for the community. I really appreciate that and am so happy I found them and took that leap of faith as when I joined my practice was just blossoming.",
        title: "wonderful reps / great experience",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/carolyn-ruutelmann.png"
      },
      {
        source: "video",
        rating: 5,
        title: null,
        body: null,
        authorName: "Amy Kaufman",
        profession: "Reiki Master Practitioner",
        locationAndDate: "Katonah, NY",
        avatarUrl: null,
        imageUrl:
          "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/amy-kaufman-preview.png",
        videoUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/amy-kaufman.mp4"
      },
      {
        source: "google_play",
        rating: 5,
        authorName: "Collete Ivanov",
        profession: "Energy worker & yoga instructor",
        locationAndDate: "Kitchener, Ontario",
        body: "As a practitioner on the network this is the best platform I've seen or used. Thank you!",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/collete-ivanov.png"
      },
      {
        source: "facebook",
        rating: null,
        authorName: "Cristy Lyn",
        profession: "Certified Hypnotherapist",
        locationAndDate: "Austin, TX",
        body: "I am so happy to be part of the practitioner network at Heal.me",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/cristy-lyn.png"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Mauro Hernandez",
        profession: "Massage Therapist",
        locationAndDate: "San Anselmo, CA",
        body: "I started using Heal Me to promote my business when they first started, about 2 years ago.\r\n\r\nI really like that they do all the work for me when it comes to exposure to my business.\r\n\r\nWhen I ask new clients how they found me they say they found me via Google, which I know that is due to Heal Me.\r\n\r\nIf you are looking for a way to promote your therapy self-own business I totally recommend Heal Me.\r\n\r\nI love the convenience of how clients are able to book and pay online as well as write reviews after their services. You are able to accept or decline new clients when you get alerts via text or email.\r\n\r\nThe company listens to its clients and is open to new ideas on how they can improve their services for us so we can concentrate on our skills and not on promoting our business.\r\n\r\nSo if you are looking for an easy and convenient way to book clients check out Heal Me.",
        title: "I started using Heal Me to promote my...",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/mauro-hernandez.png"
      },
      {
        source: "app_store",
        rating: 5,
        authorName: "Amy Kaufman",
        profession: "Reiki Master Practitioner",
        locationAndDate: "Katonah, NY",
        title: "Bookings",
        body: "Heal.me makes booking so easy And I love that clients get reminders for their sessions",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/amy-kaufman.png"
      },
      {
        source: "google_play",
        rating: 5,
        authorName: "Kimberlee Thomas",
        profession: "Usui Reiki Ceritfied Practitioner",
        locationAndDate: "Jonesboro, AR",
        body: "Beautiful app. The upgrades are perfect. Great job guys!",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/kimberlee-thomas.png"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Lindsay Koach",
        profession: "Integrative Nutrition Health Practitioner",
        locationAndDate: "Pittsburgh, PA",
        body: "The team has been so supportive, and responsive when there's been hiccups in getting set up. I'm so happy I joined the heal.me fam!",
        title: "Excellent Support",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/lindsay-koach.png"
      },
      {
        source: "facebook",
        rating: null,
        authorName: "Tanya Edmonds",
        profession: "Mindfulness, Positive Psychology and Wellbeing Coach",
        locationAndDate: "Glendale, CA",
        body: "Love this platform for wellness practitioners 👏",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/tanya-edmonds.png"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Sue Ann Karlson",
        profession: "Massage Therapist",
        locationAndDate: "Scottsdale, AZ",
        body: "This is a very helpful way to utilize a web site. Set up was not complicated and they are very anxious to help when help is needed. The professional photo session was a great experience and a huge addition to the website; something I probably wouldn't have done on my own.",
        title: "Great Experience",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/sue-ann-karlson.png"
      },
      {
        source: "google_play",
        rating: 5,
        authorName: "Collete Ivanov",
        profession: "Energy worker & yoga instructor",
        locationAndDate: "Kitchener, Ontario",
        body: "As a practitioner on the network this is the best platform I've seen or used. Thank you!",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/collete-ivanov.png"
      },
      {
        source: "instagram",
        rating: null,
        authorName: "Jvon Johnson",
        profession: "Certified Reiki Master",
        locationAndDate: "Chicago, IL",
        body: "I am on there!",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/jvon-johnson.png"
      },
      {
        source: "video",
        rating: 5,
        title: null,
        body: null,
        authorName: "Mauro Hernandez",
        profession: "Mobile massage therapist",
        locationAndDate: "San Francisco Bay Area, CA",
        avatarUrl: null,
        imageUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/mauro.png",
        videoUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/mauro.mp4"
      },
      {
        source: "facebook",
        rating: null,
        authorName: "April V. Madsen",
        profession: "mental health professional and certified energy healer",
        locationAndDate: "Sandy, UT",
        body: "Love being part of this network of amazing people!",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/april-v-madsen.png"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Valentina Gadjaloff",
        profession: "Clinical Aromatherapist",
        locationAndDate: "Guelph, ON",
        body: "This is exactly what the alternative industry needs! What a contribution this is for my and all of the Natural Health Practitioners and teachers.With gratitude,\r\nValentina\r\nAromatic Wholistic Health Spa",
        title: "This is exactly what the alternative...",
        avatarUrl:
          "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/valentina-gadjaloff.png"
      },
      {
        source: "video",
        rating: 5,
        title: null,
        body: null,
        authorName: "Brenda Romanowski",
        profession: "Nutrition Coach",
        locationAndDate: "Winston-Salem, North Carolina",
        avatarUrl: null,
        imageUrl:
          "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/brenda-romanowski-preview.png",
        videoUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/brenda-romanowski.mp4"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Melodee Von",
        profession: "Access Bars® Facilitator",
        locationAndDate: "Newberg, OR",
        body: "Heal.me is one the greatest tools in my business tool box! As a global listing service for every Holistic practice, it's the only one of its kind! It's all in what you choose to do with it! I use this site as my main web page!\r\n\r\nTotal ease with booking, payments (the credit card fees the client pays and I receive my full session price). My reviews and credentialing are also verified and public! I send my clients here to sign up and book!\r\n\r\nI am able to adjust my sessions and pricing with ease and offer sessions when I travel. The creators and team who have created this platform for all of us are truly phenomenal. I am so grateful for all the ease, clients, and advertising this platform has brought me! How does it get any better than this? What else is possible?",
        title: "Heal.me is one the greatest tools in my business tool box!",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/melodee-von.png"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Imani Ma'at",
        profession: "Wellness Coach",
        locationAndDate: "Atlanta, GA",
        body: "I am enjoying my site on Heal.me. It is super easy to use and my clients are now signing up for appointments through this site. They have made lots of improvements recently and I highly recommend it for coaches and consultants who are serious about facilitating healing!",
        title: "Highly recommend Heal.me",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/imani-maat.png"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Ryan Shroyer",
        profession: "Massage Therapist",
        locationAndDate: "Colorado Springs, CO",
        body: "This service is invaluable to my practice! Very user-friendly scheduling interface for the practitioner and the client.",
        title: "This service is invaliable to my...",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/ryan-shroyer.png"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Rachel Pope",
        profession: "Registered Nurse",
        locationAndDate: "Mt. Juliet, TN",
        body: "I am happy with the services I've received. Their customer service is top notch! I'm a practitioner and designing my profile/website was so super easy. It's so user friendly! Literally, I had no experience with website/webpage design. It's honestly so simple to create, update and edit my profile as a practitioner. Also, the link is super easy to share or even change if you ever desire to. I really really appreciate the ease of linking my payments to my account and my appointments to my calendar. My clients are able to book appointments and pay with ease. I've never had an issue with scheduling or payments through this site. Another thing I must mention is that social media is just too much for me... advertising on social media is great but I get lost in the scroll hole every time. Heal.me helps with this because they advertise on social media for me!!! I can't tell you how many clients I've gained through this feature! They are exactly the clients I have aimed to reach. I am very satisfied with their services feels like quite an understatement! Thanks everyone at Heal.me for your kindness, encouragement, and support as I've navigated this new business!",
        title: "What an awesome business",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/rachel-pope.png"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Chris Pizzarello",
        profession: "Hypnotherapis",
        locationAndDate: "Fair Lawn, NJ",
        body: "My name is Chris Pizzarello and I am one of the Practitioners on Heal.me. I found out about Heal.me before they came out of beta testing. The website has grown so much since then.\r\n\r\nIt is a great place for people to find out about my practice. They provide an introduction to the clients for each practitioner and there are reviews posted from real clients!\r\n\r\nHeal.me has given me a way of reaching clients all over North America and provide me the opportunity to assist clients I would never have been able to meet me. The clients I have worked with have been amazing and I know that I have made significant changes in their lives.\r\n\r\nI am so excited to have a listing on Heal.me and to continue to be able to meet great clients.",
        title: "My name is Chris Pizzarello and I am...",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/chris-pizzarello.png"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Cara Gallucci",
        profession: "Reiki Master",
        locationAndDate: "Boston, MA",
        body: "I've found Heal.Me to be a company with integrity and a strong desire to serve both the professionals who list with it and the customers who use it to find services.\r\n\r\nI got on board at an early phase. It's easy to create and edit my profile as a practitioner, and I appreciate the integrated payments and scheduling. It's like having a personal assistant and business manager at my fingertips 24/7.\r\n\r\nSo far I have not been able to take full advantage of this great service, since I am on sabbatical to write and relocate my private practice. I can't wait to get started more fully. But during this time, I've been pleased with the caring communications and services I've received from Heal.Me, keeping me on track.\r\n\r\nThank you, Max!",
        title: "Heal.me is a company with integrity",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/cara-gallucci.png"
      },
      {
        source: "video",
        rating: 5,
        title: null,
        body: null,
        authorName: "Roberta Devers",
        profession: "Trauma Coach",
        locationAndDate: "Syracuse, NY",
        avatarUrl: null,
        imageUrl:
          "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/roberta-davers-preview.png",
        videoUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/roberta-davers.mp4"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Jon Galane",
        profession: "Hypnotherapist",
        locationAndDate: "Boise, ID",
        body: "I'm a satisfied professional. I have been a member since the beginning when the name was (Dacocloud). I would say as Dacocloud I received 3-4 prospect a quarter with a 90% conversion rate to clients. Since the name change I know they have had some difficulties in getting the Heal.me name out there. Prospects have slowed down, but I have faith that as people get to know the new name prospects will pick up.",
        title: "I'm a satisfied professional.",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/jon-galane.png"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Alex Novicki",
        profession: "Bodywork Practitioner",
        locationAndDate: "Austin, TX",
        title: "A tool that we've all needed.",
        body: "A tool that we've all needed. It's here! Its helping so many people get the care that they need and also helping practitioners be seen. Love love love!",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/alex-novicki.png"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Lowell Chodosh",
        profession: "Acupuncturist",
        locationAndDate: "Valley Center, CA",
        body: "I am excited to see how heal.me develops. It has great potential to become a platform for practitioners and everyone to learn, to connect, and to take control of their health.I'm looking forward to reaching out to others using heal.me as a teaching tool; and, with great anticipation, to watch the platform grow and provide ever more exciting and useful features. A shout out to Max at heal.me. Thank you. Great job.",
        title: "Looking Forward to more heal.me services",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/lowell-chodosh.png"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Brock Cannon",
        profession: "Breathwork practitioner",
        locationAndDate: "Tucson, AZ",
        body: "Heal.me has been phenomenal for me as a breathwork instructor and practitioner. Instead of spending $3k on a new website I chose to use this for my bookings. I love how the payments are easily integrated and the ability to have clients find you based on symptoms is powerful and robust. I'm not sure it's the norm, but in my first 2 weeks I had 5 bookings. Very happy. Highly recommend if you're looking to attract high quality long term clients in the wellness space.",
        title: "Heal.me has been phenomenal for me as a...",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/brock-cannon.png"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Trevor Colm",
        profession: "Chiropractor",
        locationAndDate: "Austin, TX",
        body: "This is such an amazing platform for both practitioners and clients. I have yet to see anything else compares and how well this connects holistic practitioners with people seeking their services.",
        title: "This is such an amazing platform for...",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/trevor-colm.png"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Dr. M Ryan Saldivar",
        profession: "Chiropractor & bodyworker",
        locationAndDate: "Berkeley, CA",
        body: "Heal.me has been a great resource for me to connect with new Patients that are in need of my pain management services. The real benefit is in getting my work to the people that need it the most!",
        title: "Heal.me has been a great resource for me",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/m-ryan.png"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Tarmim Khan",
        profession: "Holistic Health Practitioner",
        locationAndDate: "Chicago, IL",
        body: "Heal.me is revolutionizing and decolonizing the wellness industry. It's so important to note in this modern day and age–traditional medicine isn't always helpful. The plethora of service providers listed and unique niches of each practitioner is incredible. I've had an excellent experience with Heal Me and would highly recommend their services for providers and clients. What stands out is their commitment to integrity and authenticity. Heal Me ensures a safe environment by actively protecting against false claims, allowing clients to focus on their personal growth journey without worries. If you're seeking genuine support in improving your comprehensive wellness with folks who actually know what they're doing–check them out & you won't be disappointed.",
        title: "Revolutionizing the Wellness Industry",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/tarmim-khan.png"
      },
      {
        source: "trustpilot",
        rating: 5,
        authorName: "Ellie Campbell",
        profession: "Osteopathic Doctor",
        locationAndDate: "Cumming, GA",
        body: "Heal.me is a great place to find integrative experts. Heal.me is a great place to find integrative health and wellness care no matter where you live.",
        title: "Heal.me is a great place to find integrative experts.",
        avatarUrl: "https://healme-production-assets.s3.us-east-2.amazonaws.com/pro-landing-page/ellie-campbell.png"
      }
    ]

    if (reviews.length === 0) {
      return fallbackReviews
    }

    const videoReviews = fallbackReviews.filter((review) => review.source === "video")
    const mixedReviews = []
    let videoIndex = 0

    reviews.forEach((review, index) => {
      mixedReviews.push(review)
      if ((index + 1) % 4 === 0 && videoReviews[videoIndex]) {
        mixedReviews.push(videoReviews[videoIndex])
        videoIndex += 1
      }
    })

    return mixedReviews.concat(videoReviews.slice(videoIndex))
  }, [reviews])

  // Always render all reviews; visibility controlled by overlay height
  const hasMoreReviews = visibleHeight + 1 < gridContentHeight

  const handleShowMore = () => {
    if (isLoading) return
    setIsLoading(true)
    // Reveal all content at once - add extra padding to ensure complete reveal
    setRevealHeight(gridContentHeight + 1000)
    setTimeout(() => setIsLoading(false), 120)
  }

  // Measure full grid height; desktop: show ~2x initial area (1536px) before "Show more"
  React.useLayoutEffect(() => {
    const gridEl = gridRef.current
    if (!gridEl) return
    const full = gridEl.scrollHeight
    setGridContentHeight(full)
    if (revealHeight === 0) {
      const initial = window.innerWidth >= 1024 ? 1536 : 4096
      setRevealHeight(Math.min(initial, full))
    }
  }, [mixedReviewsArray.length])

  // Recompute visibleHeight whenever revealHeight or grid height changes
  React.useLayoutEffect(() => {
    const gridEl = gridRef.current
    if (!gridEl) return
    const full = gridEl.scrollHeight
    setGridContentHeight(full)
    const clip = Math.min(revealHeight || 0, full)
    setVisibleHeight(clip)
  }, [revealHeight])

  // Recompute on resize/orientation changes
  React.useEffect(() => {
    const recalc = () => {
      const gridEl = gridRef.current
      if (!gridEl) return
      const full = gridEl.scrollHeight
      setGridContentHeight(full)
      const clip = Math.min(revealHeight || 0, full)
      setVisibleHeight(clip)
    }
    window.addEventListener("resize", recalc)
    window.addEventListener("orientationchange", recalc)
    return () => {
      window.removeEventListener("resize", recalc)
      window.removeEventListener("orientationchange", recalc)
    }
  }, [revealHeight, mixedReviewsArray.length])

  return (
    <VideoPlayerProvider>
      <section className="pro-reviews-section">
        <div className="pro-reviews-header">
          <div className="pro-reviews-stars">
            {Array.from({ length: 5 }, (_, i) => (
              <svg key={i} className="pro-star" viewBox="0 0 24 24" fill="#008080">
                <path d="M12 2L15.09 8.26L22 9L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9L8.91 8.26L12 2Z" />
              </svg>
            ))}
          </div>
          <h2 className="pro-reviews-title">Trusted by thousands of wellness practitioners</h2>
        </div>

        <div
          className="pro-reviews-clip"
          style={{
            position: "relative",
            overflow: "hidden",
            maxHeight: visibleHeight ? `${visibleHeight}px` : "none",
            transition: "max-height 0.3s ease"
          }}>
          <div
            ref={gridRef}
            className="pro-reviews-grid"
            style={{
              opacity: isLoading ? 0.9 : 1,
              transition: "opacity 0.2s ease"
            }}>
            {mixedReviewsArray.map((review, index) => (
              <ReviewCard key={`review-${index}`} review={review} index={index} />
            ))}
          </div>
          {hasMoreReviews && (
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: "100px",
                background: "linear-gradient(transparent, white)",
                pointerEvents: "none"
              }}
            />
          )}
        </div>

        {hasMoreReviews && (
          <>
            <div className="pro-show-more-container">
              <button
                ref={showMoreButtonRef}
                className="pro-show-more-button"
                onClick={handleShowMore}
                disabled={isLoading}
                style={{
                  opacity: isLoading ? 0.6 : 1,
                  cursor: isLoading ? "not-allowed" : "pointer"
                }}>
                {isLoading ? "Loading..." : "Show more"}
              </button>
            </div>
          </>
        )}
      </section>
    </VideoPlayerProvider>
  )
}

export default ReviewsSection
